<template>
    <el-drawer
        :visible.sync="settingsVisible"
        :with-header="false"
        direction="rtl"
        :size="isMobile ? '100%' : '560px'"
        :modal="false"
        :wrapper-closable="true"
        custom-class="settings-drawer"
    >
        <div class="settings-container">
            <!-- PC端头部 -->
            <div class="settings-header">
                <div class="header-title">
                    <h2>⚙️ 设置中心</h2>
                    <p class="settings-subtitle">个性化你的小桃网体验</p>
                </div>
                <div class="header-close" @click="handleClose" title="关闭">
                    <i class="el-icon-close"></i>
                </div>
            </div>

            <!-- 移动端顶部导航 -->
            <div class="mobile-header">
                <span class="mobile-back" @click="handleClose">
                    <i class="el-icon-arrow-left"></i>
                </span>
                <span class="mobile-title">{{ getCategoryLabel(activeSetting) }}</span>
                <span class="mobile-close" @click="handleClose">完成</span>
            </div>

            <!-- PC端导航 -->
            <div class="settings-nav">
                <div
                    v-for="item in settingsCategories"
                    :key="item.key"
                    :class="['nav-item', { active: activeSetting === item.key }]"
                    @click="activeSetting = item.key"
                >
                    <i :class="item.icon"></i>
                    <span>{{ item.label }}</span>
                </div>
            </div>

            <div class="settings-content">
                <PersonalData v-if="activeSetting === 'profile'" />
                <AccountSecurity v-if="activeSetting === 'security'" />
                <ThemeSetting v-if="activeSetting === 'theme'" />
                <BackgroundSetting v-show="activeSetting === 'background'" />
                <MessageNotice v-if="activeSetting === 'notice'" />
                <GeneralSetting v-if="activeSetting === 'general'" />
                <PrivacySetting v-if="activeSetting === 'privacy'" />
                <AboutUs v-if="activeSetting === 'about'" />
            </div>

            <!-- 移动端底部导航 -->
            <div class="mobile-footer-nav">
                <div
                    v-for="item in settingsCategories"
                    :key="item.key"
                    :class="['mobile-nav-item', { active: activeSetting === item.key }]"
                    @click="activeSetting = item.key"
                >
                    <i :class="item.icon"></i>
                    <span>{{ item.label }}</span>
                </div>
            </div>

            <div class="settings-footer">
                <el-button
                    type="danger"
                    plain
                    round
                    icon="el-icon-switch-button"
                    @click="handleLogoutFromSettings"
                >退出登录</el-button>
            </div>
        </div>
    </el-drawer>
</template>

<script>
import PersonalData from '@/components/Set/PersonalData.vue';
import AccountSecurity from '@/components/Set/AccountSecurity.vue';
import ThemeSetting from '@/components/Set/ThemeSetting.vue';
import BackgroundSetting from '@/components/Set/BackgroundSetting.vue';
import MessageNotice from '@/components/Set/MessageNotice.vue';
import GeneralSetting from '@/components/Set/GeneralSetting.vue';
import PrivacySetting from '@/components/Set/PrivacySetting.vue';
import AboutUs from '@/components/Set/AboutUs.vue';

export default {
    name: 'SettingsPanel',
    components: {
        PersonalData,
        AccountSecurity,
        ThemeSetting,
        BackgroundSetting,
        MessageNotice,
        GeneralSetting,
        PrivacySetting,
        AboutUs
    },
    data() {
        return {
            settingsVisible: false,
            activeSetting: 'profile',
            settingsCategories: [
                { key: 'profile', label: '个人资料', icon: 'el-icon-user' },
                { key: 'security', label: '账号安全', icon: 'el-icon-lock' },
                { key: 'theme', label: '主题设置', icon: 'el-icon-brush' },
                { key: 'background', label: '背景设置', icon: 'el-icon-picture' },
                { key: 'notice', label: '消息通知', icon: 'el-icon-bell' },
                { key: 'general', label: '通用设置', icon: 'el-icon-set-up' },
                { key: 'privacy', label: '隐私设置', icon: 'el-icon-key' },
                { key: 'about', label: '关于我们', icon: 'el-icon-info' }
            ],
            isMobileWidth: false
        };
    },
    computed: {
        isMobile() {
            return this.isMobileWidth || window.innerWidth <= 768;
        }
    },
    mounted() {
        this.$bus.$on('open-settings', this.handleOpenSettings);
        this.$bus.$on('close-settings', this.handleClose);
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        this.$bus.$off('open-settings', this.handleOpenSettings);
        this.$bus.$off('close-settings', this.handleClose);
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.isMobileWidth = window.innerWidth <= 768;
        },
        getCategoryLabel(key) {
            const category = this.settingsCategories.find(c => c.key === key);
            return category ? category.label : '设置中心';
        },
        handleOpenSettings(category) {
            if (category && category !== 'all') {
                const matched = this.settingsCategories.find(c => c.key === category);
                if (matched) {
                    this.activeSetting = category;
                } else {
                    this.activeSetting = 'profile';
                }
            } else {
                this.activeSetting = 'profile';
            }
            this.settingsVisible = true;
        },
        handleClose() {
            this.settingsVisible = false;
        },
        handleLogoutFromSettings() {
            this.$confirm('确定退出登录吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                localStorage.removeItem('userInfo');
                this.settingsVisible = false;
                this.$message.success('已退出登录');
                this.$router.push('/login');
            }).catch(() => {});
        }
    }
};
</script>

<style scoped>
.settings-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--background-color);
}

/* PC端头部 */
.settings-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px 24px 20px;
    background: linear-gradient(135deg, var(--theme-color, #409EFF), var(--theme-color-light, #79bbff));
    color: #fff;
}

.settings-header .header-title {
    flex: 1;
}

.settings-header h2 {
    margin: 0 0 6px 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1px;
}

.settings-header .settings-subtitle {
    margin: 0;
    font-size: 13px;
    opacity: 0.9;
}

.settings-header .header-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    font-size: 18px;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.15);
    transition: background 0.2s, color 0.2s;
    flex-shrink: 0;
}

.settings-header .header-close:hover {
    background: rgba(255, 255, 255, 0.3);
    color: #fff;
}

/* 移动端顶部导航 */
.mobile-header {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 56px;
    padding: 0 16px;
    background: var(--card-background);
    border-bottom: 1px solid var(--border-color-lighter);
    align-items: center;
    justify-content: space-between;
    z-index: 10;
}
.mobile-back,
.mobile-close {
    font-size: 14px;
    color: var(--theme-color);
    cursor: pointer;
    padding: 8px;
    -webkit-tap-highlight-color: transparent;
}
.mobile-close {
    font-weight: 500;
}
.mobile-title {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-color);
}

/* PC端导航 */
.settings-nav {
    display: flex;
    flex-wrap: wrap;
    padding: 16px 20px;
    gap: 8px;
    background: var(--card-background);
    border-bottom: 1px solid var(--border-color-lighter);
}

.settings-nav .nav-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 20px;
    background: var(--background-color-soft);
    color: var(--text-color-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid transparent;
}

.settings-nav .nav-item:hover {
    background: var(--theme-color-light);
    color: var(--theme-color, #409EFF);
}

.settings-nav .nav-item.active {
    background: var(--theme-color, #409EFF);
    color: #fff;
    box-shadow: 0 2px 8px var(--theme-color-light);
}

.settings-nav .nav-item i {
    font-size: 14px;
}

.settings-content {
    flex: 1;
    padding: 24px;
    overflow-y: auto;
    overflow-x: hidden;
    background: var(--background-color);
    /* 美化滚动条 */
    scrollbar-width: thin;
    scrollbar-color: var(--theme-color-light, #79bbff) transparent;
}
.settings-content::-webkit-scrollbar {
    width: 8px;
    background: transparent;
}
.settings-content::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
}
.settings-content::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, var(--theme-color-light, #79bbff), var(--theme-color, #409EFF));
    border-radius: 4px;
    transition: background 0.3s;
}
.settings-content::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, var(--theme-color, #409EFF), #2d7fb8);
}
.settings-content::-webkit-scrollbar-corner {
    background: transparent;
}

/* 移动端底部导航 */
.mobile-footer-nav {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: var(--card-background);
    border-top: 1px solid var(--border-color-lighter);
    padding: 0 4px;
    padding-bottom: env(safe-area-inset-bottom, 0);
    z-index: 100;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}
.mobile-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 6px 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    color: var(--text-color-muted);
    transition: color 0.2s;
}
.mobile-nav-item i {
    font-size: 20px;
    margin-bottom: 2px;
}
.mobile-nav-item span {
    font-size: 10px;
}
.mobile-nav-item.active {
    color: var(--theme-color);
}
.mobile-nav-item:active {
    background: var(--background-color-soft);
}

.settings-footer {
    padding: 20px 24px 24px;
    border-top: 1px solid var(--border-color-lighter);
    background: var(--card-background);
}

.settings-footer .el-button {
    width: 100%;
    padding: 10px 0;
}

/* 响应式适配 */
@media (max-width: 768px) {
    .settings-header {
        display: none;
    }
    .settings-nav {
        display: none;
    }
    .settings-content {
        padding: 16px;
        padding-top: 72px;
        padding-bottom: 80px;
    }
    .settings-footer {
        padding: 16px;
        padding-bottom: calc(16px + env(safe-area-inset-bottom, 0) + 60px);
    }
    .mobile-header {
        display: flex;
    }
    .mobile-footer-nav {
        display: flex;
    }
}

@media (max-width: 375px) {
    .mobile-nav-item span {
        font-size: 9px;
    }
    .mobile-nav-item i {
        font-size: 18px;
    }
}
</style>

<style>
.settings-drawer {
    background: var(--background-color);
    /* 降低 drawer 层级，确保内部弹窗能正常显示 */
}
.settings-drawer .el-drawer__body {
    background: var(--background-color);
    padding: 0;
    /* 关键：确保内部弹窗不会被 drawer 容器裁剪/遮挡 */
    overflow: visible;
}

/* 抽屉主容器：设置合理层级，让内部弹窗可以覆盖 */
.el-drawer__wrapper.settings-drawer__wrapper,
.settings-drawer ~ .v-modal,
.el-drawer__wrapper {
    z-index: 2200 !important;
}

.el-drawer__wrapper ~ .v-modal {
    z-index: 2199 !important;
}
</style>
